import { ElementNode } from 'lexical';
import { createCommand } from 'lexical';

// ── Commands ──────────────────────────────────────────────────────────────────

export const INSERT_LAYOUT_COMMAND = createCommand('INSERT_LAYOUT_COMMAND');
export const UPDATE_LAYOUT_COMMAND = createCommand('UPDATE_LAYOUT_COMMAND');

// ── LayoutContainerNode ───────────────────────────────────────────────────────
// Outer grid wrapper. templateColumns is a CSS grid-template-columns value,
// e.g. "1fr 1fr" or "1fr 2fr 1fr".

export class LayoutContainerNode extends ElementNode {
  __templateColumns;

  static getType() {
    return 'layout-container';
  }

  static clone(node) {
    return new LayoutContainerNode(node.__templateColumns, node.__key);
  }

  constructor(templateColumns, key) {
    super(key);
    this.__templateColumns = templateColumns;
  }

  createDOM() {
    const dom = document.createElement('div');
    dom.style.display = 'grid';
    dom.style.gridTemplateColumns = this.__templateColumns;
    dom.style.gap = '1em';
    dom.setAttribute('data-lexical-layout-container', 'true');
    return dom;
  }

  updateDOM(prevNode, dom) {
    if (prevNode.__templateColumns !== this.__templateColumns) {
      dom.style.gridTemplateColumns = this.__templateColumns;
    }
    return false;
  }

  exportDOM() {
    const dom = document.createElement('div');
    dom.style.display = 'grid';
    dom.style.gridTemplateColumns = this.__templateColumns;
    dom.style.gap = '1em';
    return { element: dom };
  }

  static importDOM() {
    return {
      div: (node) => {
        if (!node.hasAttribute('data-lexical-layout-container')) return null;
        return {
          conversion: (domNode) => ({
            node: $createLayoutContainerNode(domNode.style.gridTemplateColumns || '1fr'),
          }),
          priority: 1,
        };
      },
    };
  }

  exportJSON() {
    return {
      ...super.exportJSON(),
      type: 'layout-container',
      templateColumns: this.__templateColumns,
      version: 1,
    };
  }

  static importJSON(json) {
    return $createLayoutContainerNode(json.templateColumns);
  }

  getTemplateColumns() {
    return this.getLatest().__templateColumns;
  }

  setTemplateColumns(templateColumns) {
    const w = this.getWritable();
    w.__templateColumns = templateColumns;
    return w;
  }

  isShadowRoot() {
    return false;
  }

  canIndent() {
    return false;
  }
}

export function $createLayoutContainerNode(templateColumns) {
  return new LayoutContainerNode(templateColumns);
}

export function $isLayoutContainerNode(node) {
  return node instanceof LayoutContainerNode;
}

// ── LayoutItemNode ────────────────────────────────────────────────────────────
// Each individual column inside a LayoutContainerNode.

export class LayoutItemNode extends ElementNode {
  static getType() {
    return 'layout-item';
  }

  static clone(node) {
    return new LayoutItemNode(node.__key);
  }

  createDOM() {
    const dom = document.createElement('div');
    dom.setAttribute('data-lexical-layout-item', 'true');
    return dom;
  }

  updateDOM() {
    return false;
  }

  exportDOM() {
    const dom = document.createElement('div');
    dom.style.minWidth = '0'; // prevent grid blowout
    return { element: dom };
  }

  static importDOM() {
    return {
      div: (node) => {
        if (!node.hasAttribute('data-lexical-layout-item')) return null;
        return {
          conversion: () => ({ node: $createLayoutItemNode() }),
          priority: 1,
        };
      },
    };
  }

  exportJSON() {
    return {
      ...super.exportJSON(),
      type: 'layout-item',
      version: 1,
    };
  }

  static importJSON() {
    return $createLayoutItemNode();
  }

  isShadowRoot() {
    return false;
  }

  canIndent() {
    return false;
  }
}

export function $createLayoutItemNode() {
  return new LayoutItemNode();
}

export function $isLayoutItemNode(node) {
  return node instanceof LayoutItemNode;
}
